/** Browser metadata never contains the draft text or media; SQL owns the turn. */
export interface DurableChatTurn<TMessage> {
  id: number;
  thread_id: number;
  status: "queued" | "running" | "completed" | "failed" | "uncertain";
  user_message_id: number;
  assistant_message: TMessage | null;
  error_code: string | null;
}

export function turnIsActive(turn: { status: string } | null | undefined): boolean {
  return turn?.status === "queued" || turn?.status === "running";
}

export function durableChatErrorMessage(code: string | null): string {
  if (code === "guard.blocked") return "Сообщение заблокировано фильтром безопасности";
  if (code === "chat.context_unavailable") {
    return "Не удалось загрузить контекст респондента. Повторите попытку позже";
  }
  return "Ошибка получения ответа";
}

interface AdmissionMetadata { hash: string; key: string; expected_last_message_id: number | null }
const memory = new Map<string, AdmissionMetadata>();

/** Preserve the original causal anchor when a response is lost and history refetches. */
export async function durableChatAdmission(scope: string, threadId: number, body: { expected_last_message_id: number | null } & Record<string, unknown>): Promise<{ client_key: string; expected_last_message_id: number | null }> {
  const { expected_last_message_id, ...input } = body;
  const bytes = new TextEncoder().encode(JSON.stringify(input));
  const hash = [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))]
    .map((value) => value.toString(16).padStart(2, "0")).join("");
  const storageKey = `chat-admission:${scope}:${threadId}`;
  let saved = memory.get(storageKey);
  try { saved = JSON.parse(localStorage.getItem(storageKey) ?? "null") as AdmissionMetadata | undefined ?? saved; } catch { /* unavailable or malformed metadata */ }
  if (saved?.hash !== hash || typeof saved.key !== "string" || !(saved.expected_last_message_id === null || Number.isInteger(saved.expected_last_message_id))) {
    saved = { hash, key: crypto.randomUUID().replaceAll("-", ""), expected_last_message_id };
  }
  memory.set(storageKey, saved);
  if (memory.size > 256) memory.delete(memory.keys().next().value!);
  try { localStorage.setItem(storageKey, JSON.stringify(saved)); } catch { /* same-tab metadata survives storage failure */ }
  return { client_key: saved.key, expected_last_message_id: saved.expected_last_message_id };
}

export async function durableChatKey(scope: string, threadId: number, body: unknown): Promise<string> {
  const input = body as Record<string, unknown>;
  return (await durableChatAdmission(scope, threadId, { ...input, expected_last_message_id: typeof input.expected_last_message_id === "number" ? input.expected_last_message_id : null })).client_key;
}

export function clearDurableChatKey(scope: string, threadId: number): void {
  memory.delete(`chat-admission:${scope}:${threadId}`);
  try { localStorage.removeItem(`chat-admission:${scope}:${threadId}`); } catch { /* storage unavailable */ }
}
